/**
 * Analytics computation helpers (pure functions).
 *
 * Source data:
 *   - gps_tracks points: { lat, lng, s (speed kph), t (epoch ms) }  → trips, stops, distance, speed
 *   - cars: status + gps* fields                                     → fleet KPIs, vehicle health
 *   - breakdowns / vidanges / mokhalafat                            → maintenance, alerts, behavior proxy
 *
 * Modules that need hardware the providers don't expose (AdBlue level, deep
 * engine ECU metrics, OEM driver scoring) are derived as best-effort proxies or
 * surfaced as "no device data" by the page.
 */

const R = 6371; // km
export const haversineKm = (aLat, aLng, bLat, bLng) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat), dLng = toRad(bLng - aLng);
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
};

const STOP_SPEED = 3;          // kph below which the vehicle is "stopped"
const STOP_MIN_MS = 3 * 60000; // a stop must last ≥ 3 min
const TRIP_GAP_MS = 10 * 60000;// gap that splits two trips

/**
 * Derive trips, stops and aggregate motion stats from an ordered point list.
 */
export const computeTripsStops = (points = []) => {
  const pts = [...points].filter(p => p && p.t).sort((a, b) => a.t - b.t);
  const trips = [], stops = [];
  let cur = null, stopStart = null, totalKm = 0, movingMs = 0, maxSpeed = 0;

  for (let i = 0; i < pts.length; i++) {
    const p = pts[i], prev = pts[i - 1];
    maxSpeed = Math.max(maxSpeed, p.s || 0);

    if (prev) {
      const dt = p.t - prev.t;
      const dKm = haversineKm(prev.lat, prev.lng, p.lat, p.lng);
      // New trip if a long gap.
      if (dt > TRIP_GAP_MS && cur) { trips.push(finalizeTrip(cur)); cur = null; }
      if ((p.s || 0) > STOP_SPEED || dKm > 0.03) {
        if (!cur) cur = { start: prev.t, end: p.t, km: 0, max: 0, pts: [prev] };
        cur.end = p.t; cur.km += dKm; cur.max = Math.max(cur.max, p.s || 0); cur.pts.push(p);
        totalKm += dKm; movingMs += dt;
        if (stopStart) {
          const dur = prev.t - stopStart.t;
          if (dur >= STOP_MIN_MS) stops.push({ start: stopStart.t, end: prev.t, durationMs: dur, lat: stopStart.lat, lng: stopStart.lng });
          stopStart = null;
        }
      } else {
        if (!stopStart) stopStart = prev;
      }
    }
  }
  if (cur) trips.push(finalizeTrip(cur));
  if (stopStart) {
    const last = pts[pts.length - 1];
    const dur = last.t - stopStart.t;
    if (dur >= STOP_MIN_MS) stops.push({ start: stopStart.t, end: last.t, durationMs: dur, lat: stopStart.lat, lng: stopStart.lng });
  }
  return { trips, stops, totalKm, movingMs, maxSpeed };
};

const finalizeTrip = (c) => {
  const durationMs = Math.max(0, c.end - c.start);
  const avg = durationMs > 0 ? (c.km / (durationMs / 3600000)) : 0;
  return { start: c.start, end: c.end, distanceKm: c.km, durationMs, maxSpeed: c.max, avgSpeed: avg };
};

/** Speed distribution buckets (kph). */
export const speedBuckets = (points = []) => {
  const edges = [0, 20, 40, 60, 80, 100, 120, Infinity];
  const labels = ['0-20', '20-40', '40-60', '60-80', '80-100', '100-120', '120+'];
  const counts = new Array(labels.length).fill(0);
  points.forEach(p => {
    const s = p.s || 0;
    for (let i = 0; i < edges.length - 1; i++) {
      if (s >= edges[i] && s < edges[i + 1]) { counts[i]++; break; }
    }
  });
  return { labels, counts };
};

/** Distance & trips per calendar day. */
export const dailySeries = (trips = []) => {
  const map = {};
  trips.forEach(t => {
    const day = new Date(t.start).toISOString().slice(0, 10);
    if (!map[day]) map[day] = { km: 0, trips: 0 };
    map[day].km += t.distanceKm; map[day].trips += 1;
  });
  const days = Object.keys(map).sort();
  return {
    days,
    km: days.map(d => +map[d].km.toFixed(1)),
    trips: days.map(d => map[d].trips),
  };
};

/**
 * Driver behaviour proxy score (0-100). Penalises speeding samples, harsh
 * accel/brake (large speed deltas between consecutive points) and violations.
 */
export const driverBehavior = (points = [], { speedLimit = 120, violations = 0 } = {}) => {
  let speeding = 0, harshAccel = 0, harshBrake = 0;
  const sorted = [...points].sort((a, b) => a.t - b.t);
  for (let i = 1; i < sorted.length; i++) {
    const s = sorted[i].s || 0, ps = sorted[i - 1].s || 0;
    const dt = (sorted[i].t - sorted[i - 1].t) / 1000;
    if (s > speedLimit) speeding++;
    if (dt > 0 && dt < 120) {
      const dv = s - ps;
      if (dv > 25) harshAccel++;
      if (dv < -30) harshBrake++;
    }
  }
  const events = speeding + harshAccel + harshBrake + violations * 3;
  const score = Math.max(0, Math.min(100, 100 - events * 2));
  return { score, speeding, harshAccel, harshBrake, violations, events };
};

/** Fleet status breakdown from the cars list. */
export const fleetStatus = (cars = []) => {
  const c = { available: 0, rented: 0, maintenance: 0, other: 0 };
  cars.forEach(v => {
    if (v.status === 'available') c.available++;
    else if (v.status === 'rented') c.rented++;
    else if (v.status === 'maintenance') c.maintenance++;
    else c.other++;
  });
  return c;
};

/** Online/offline by GPS last-seen (>15 min = offline). */
export const deviceActivity = (cars = []) => {
  const now = Date.now();
  let online = 0, offline = 0, never = 0;
  cars.forEach(v => {
    if (!v.gpsLastSeen) { never++; return; }
    const age = now - new Date(v.gpsLastSeen).getTime();
    if (age < 15 * 60000) online++; else offline++;
  });
  return { online, offline, never };
};

/** Top-level KPI cards from fleet + records. */
export const fleetKpis = (cars = [], { breakdowns = [], vidanges = [], violations = [] } = {}) => {
  const totalKm = cars.reduce((s, c) => s + (Number(c.gpsOdometer) || 0), 0);
  const dailyKm = cars.reduce((s, c) => s + (Number(c.gpsKmDaily) || 0), 0);
  const moving = cars.filter(c => (Number(c.gpsSpeed) || 0) > 3).length;
  const act = deviceActivity(cars);
  const openBreak = breakdowns.filter(b => (b.status || 'open') === 'open').length;
  return {
    vehicles: cars.length,
    online: act.online,
    moving,
    inRepair: cars.filter(c => c.status === 'maintenance').length,
    openBreakdowns: openBreak,
    totalKm: Math.round(totalKm),
    dailyKm: +dailyKm.toFixed(1),
    maintenanceCount: vidanges.length,
    alerts: violations.length + openBreak,
  };
};

export const fmtDuration = (ms, lang = 'fr') => {
  const m = Math.round((ms || 0) / 60000);
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), mm = m % 60;
  const u = lang === 'ar' ? { d: 'ي', h: 'س', m: 'د' } : { d: 'j', h: 'h', m: 'min' };
  const parts = [];
  if (d) parts.push(`${d}${u.d}`);
  if (h) parts.push(`${h}${u.h}`);
  if (mm || (!d && !h)) parts.push(`${mm}${u.m}`);
  return parts.join(' ');
};
